import { 
    Button, 
    DateRangePicker,
    Container, 
    FormField, 
    Select, 
    Table, 
    Header, 
    SpaceBetween, 
    PropertyFilter,
    Pagination,
    TextContent,
    Box
} from '@amzn/awsui-components-react';
import { get } from 'lodash';
import moment from 'moment';
import React, { useState, useMemo } from 'react';
import { useExecutionExplanationQuery } from '../../../hooks/Worker/useGetExecutionExplanationQuery';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { PROPERTY_FILTERING_I18N_CONSTANTS } from '../../../../utils/constants';
import { EmptyState, getFilterCounterText, Preferences } from "../../../../utils/tablePreferences";
import {ExportCSV} from "../../../../utils/exportCSV";
import { isValidRange, convertTZ } from '../../../../utils/timeUtils';

import {
    i18nStrings,
    relativeOptions,
    timezoneOptions,
} from './../AuditHistory/utils';

const columns = [
    {
        id: 'Action',
        header: 'Action',
        cell: (e) => (e['Action'] ? e['Action'] : ''),
        sortingField: 'Action',
    },
    {
        id: 'Explanation',
        header: 'Explanation',
        cell: (e) => {
            try {
                if (!e['Explanation']) return '';
                if (typeof e['Explanation'] !== 'string') return '';
                
                const text = e['Explanation'];
                return (
                    <div style={{ whiteSpace: 'pre-line' }}>
                        {text
                            .replace(/(\w+ScaleIn:)/g, '\n$1')
                            .replace(/(worker not in to reap plan:)/g, '\n$1')
                            .replace(/suppress_scale_in is \[.*?\]\s*/g, '') // Remove suppress_scale_in line
                            .replace(/\n\s*\n+/g, '\n') // Remove multiple empty lines
                            .trim() || ''}
                    </div>
                );
            } catch (error) {
                console.error('Error processing Explanation:', error);
                return <div>Error processing explanation</div>;
            }
        },
        sortingField: 'Explanation'
    },
    {
        id: 'SuppressScaleIn',
        header: 'Suppress Scale In',
        cell: (e) => {
            try {
                if (!e['Explanation']) return '';
                const match = e['Explanation'].match(/suppress_scale_in is \[(.*?)\]/);
                return match ? match[1] : '';
            } catch (error) {
                console.error('Error processing SuppressScaleIn:', error);
                return '';
            }
        },
        sortingField: 'SuppressScaleIn',
    },
];

const executionExplanationVisibleContent = [
    'EventTimestamp',
    'Action',
    'Explanation',
    'SuppressScaleIn',
];

const FILTERING_PROPERTIES = executionExplanationVisibleContent.map(colId => ({
    propertyLabel: colId,
    key: colId,
    groupValuesLabel: `${colId} values`,
    operators: [':', '!:', '=', '!='],
}));

const VISIBLE_CONTENT_OPTIONS = [
    {
        label: 'Main properties',
        options: executionExplanationVisibleContent.map(colId => ({ 
            id: colId, 
            label: colId 
        }))
    }
];

const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10 Execution Explanation Records' },
    { value: 20, label: '20 Execution Explanation Records' },
    { value: 50, label: '50 Execution Explanation Records' },
    { value: 100, label: '100 Execution Explanation Records' },
];

const DEFAULT_PREFERENCES = {
    pageSize: 10,
    visibleContent: executionExplanationVisibleContent.map(col => col),
    wraplines: true,
};

const ExecutionExplanation = (props) => {
    const [dataPlaneType, setDataPlaneType] = useState<string>('indexing');
    const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

    const [currentDate, setCurrentDate] = React.useState<any>({
            type: 'relative',
            amount: 10,
            unit: 'minute',
        });

    const [selectedTimeZoneOption, setSelectedTimeZoneOption] = React.useState({
            inputDisplay: 'Browser Timezone',
            label: 'Browser Timezone',
            value: 'Browser Timezone',
            code: '',
        });

    const limitCount = 10000;

    const [executeExecutionExplanation, { loading, error, data }] = useExecutionExplanationQuery();

    const tableItems = useMemo(() => {
        if (data?.getExecutionExplanation) {
            return Array.isArray(data.getExecutionExplanation) 
                ? data.getExecutionExplanation.map(item => {
                    // Extract SuppressScaleIn value and add it as a separate property
                    const match = item['Explanation']?.match(/suppress_scale_in is \[(.*?)\]/);
                    const suppressScaleIn = match ? match[1] : '';
                    
                    return {
                        ...item,
                        SuppressScaleIn: suppressScaleIn
                    };
                })
                : [];
        }
        return [];
    }, [data]);

    
    const handleTimezoneChange = (selectedOption) => {
        setSelectedTimeZoneOption(selectedOption);
    };

    const handleDateChange = ({ detail }) => {
        try {
            var startMoment, endMoment;

            if ('absolute' === detail.value.type) {
                startMoment = moment(detail.value.startDate);
                endMoment = moment(detail.value.endDate);
            } else if ('relative' === detail.value.type) {
                startMoment = moment().subtract(detail.value.amount, detail.value.unit);
                endMoment = moment();
            }

            executeExecutionExplanation({
                variables: {
                    placementConstraintId: props.placementConstraintId,
                    dataPlaneType: dataPlaneType,
                    startTime: startMoment.valueOf().toString(),
                    endTime: endMoment.valueOf().toString(),
                    limit: limitCount,
                },
            });

            setCurrentDate(detail.value);
        } catch (err) {
            console.error('Error executing execution explanation command during dateChange:', err);
        }
    };

    let events = get(ExecutionExplanation, 'executeExecutionExplanation', []);

    let showLimitMessage = false;
    if (events.length > 0) {
        showLimitMessage = events[events.length - 1]['resultTruncated']
            ? true
            : false;
    }

    const {
        items,
        actions,
        filteredItemsCount,
        collectionProps,
        propertyFilterProps,
        paginationProps,
    } = useCollection(tableItems, {
        propertyFiltering: {
            filteringProperties: FILTERING_PROPERTIES,
            empty: <EmptyState title="No Execution Explanations" subtitle="No Execution Explanations to display." action={<span></span>} />,
            noMatch: (
                <EmptyState
                    title="No matches"
                    subtitle="We can't find a match."
                    action={<Button onClick={() => actions.setPropertyFiltering({ tokens: [], operation: 'and' })}>Clear filter</Button>}
                />
            ),
        },
        pagination: { pageSize: preferences.pageSize },
        sorting: {},
    });

    const handleExecute = async () => {
        try {
            let startMoment, endMoment;

            if ('absolute' === currentDate.type) {
                startMoment = moment(currentDate.startDate);
                endMoment = moment(currentDate.endDate);
            } else if ('relative' === currentDate.type) {
                startMoment = moment().subtract(currentDate.amount, currentDate.unit);
                endMoment = moment();
            } else {
                // Default to last 10 minutes if no date range is selected
                startMoment = moment().subtract(10, 'minutes');
                endMoment = moment();
            }

            await executeExecutionExplanation({
                variables: {
                    placementConstraintId: props.placementConstraintId,
                    dataPlaneType: dataPlaneType,
                    startTime: startMoment.valueOf().toString(),
                    endTime: endMoment.valueOf().toString(),
                    limit: limitCount,
                }
            });
        } catch (err) {
            console.error('Error executing execution explanation command:', err);
        }
    };


    return (
        <Container>
            <SpaceBetween direction="vertical" size="l">
                <FormField label="Data Plane Type">
                    <Select
                        selectedOption={{ 
                            value: dataPlaneType, 
                            label: dataPlaneType.charAt(0).toUpperCase() + dataPlaneType.slice(1) 
                        }}
                        onChange={({ detail }) => setDataPlaneType(detail.selectedOption.value)}
                        options={[
                            { value: 'search', label: 'Search' },
                            { value: 'indexing', label: 'Indexing' }
                        ]}
                    />
                </FormField>
                <Button onClick={handleExecute} loading={loading}>
                    Execute
                </Button>
                
                <Table
                    {...collectionProps}
                    header={
                        <div>
                            <Header
                                counter={`(${items.length} of ${filteredItemsCount})`}
                                actions={
                                    <SpaceBetween direction="horizontal" size="s">
                                        <DateRangePicker
                                            onChange={handleDateChange}
                                            value={currentDate}
                                            expandToViewport={true}
                                            relativeOptions={relativeOptions}
                                            isValidRange={isValidRange}
                                            i18nStrings={i18nStrings}
                                            placeholder="Filter by a date and time range"
                                        />
                                        <TextContent>
                                            <strong>Timezone :</strong>
                                        </TextContent>
                                        <Select
                                            selectedOption={selectedTimeZoneOption}
                                            onChange={({ detail }) =>
                                                handleTimezoneChange(detail.selectedOption)
                                            }
                                            options={timezoneOptions}
                                            selectedAriaLabel="Selected"
                                        />
                                        <ExportCSV
                                            data={items}
                                            filename={props.csvExportFileName}
                                            loading={false}
                                        />
                                    </SpaceBetween>
                                }
                            >
                                Execution Explanation
                            </Header>
                            {showLimitMessage && (
                                <p>
                                    There are more than {events.length - 1} actions and we
                                    are displaying the latest {events.length - 1} actions
                                </p>
                            )}
                        </div>
                    }
                    columnDefinitions={[
                        {
                            id: 'EventTimestamp',
                            header: 'Event Timestamp',
                            sortingField: 'TimeStamp',
                            cell: (e) => {
                                var timeStamp = e['TimeStamp'];
                                if (timeStamp && typeof timeStamp === 'string') {
                                    const timestamp = moment(timeStamp).valueOf();
                                    return convertTZ(timestamp, selectedTimeZoneOption);
                                }
                            },
                        },
                        ...columns,
                    ]}
                    stickyHeader={true}
                    resizableColumns={true}
                    wrapLines={preferences.wraplines}
                    loading={loading}
                    loadingText={
                        loading
                            ? 'Loading records...'
                            : 'There were no records.'
                    }
                    visibleColumns={preferences.visibleContent}
                    items={items}
                    pagination={
                        <Pagination
                            {...paginationProps}
                            ariaLabels={{
                                nextPageLabel: 'Next page',
                                previousPageLabel: 'Previous page',
                                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
                            }}
                        />
                    }
                    filter={
                        <div className="input-container">
                            <PropertyFilter
                                className="input-filter"
                                enableTokenGroups
                                expandToViewport={true}
                                i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                                {...propertyFilterProps}
                                countText={getFilterCounterText(filteredItemsCount)}
                            />
                        </div>
                    }
                    preferences={
                        <Preferences
                            preferences={preferences}
                            setPreferences={setPreferences}
                            disabled={false}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            visibleContentOptions={VISIBLE_CONTENT_OPTIONS}
                        />
                    }
                />
            </SpaceBetween>
        </Container>
    );
};

export { ExecutionExplanation };
